/**
 * filterRailCatalog.ts
 *
 * Scope resolution + tiering for the advanced filter editor's property rail.
 *
 * The rail is deliberately NOT "all properties". It shows the properties that
 * matter for the types the filter is actually about:
 *
 *  - Core        — universal properties (on every sphere), plus custom
 *                  properties every in-scope type declares.
 *  - Per type    — one section per in-scope type, listing that type's own
 *                  custom properties.
 *
 * Searching escapes that scope on purpose: a query runs against the whole
 * org-wide catalog so a property on a type you have not scoped to is still
 * findable — it is just marked out of scope.
 */
import type { PropertyOption } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';
import {
  collectFilterScopeLeaves,
  inferEligibleSchemas,
  type SchemaForFilters,
} from './filterPropertyBuilder';

/** Where the rail's type scope came from — drives the banner copy. */
export type RailScopeSource = 'filter' | 'level';

export interface RailScope {
  /** Concrete schema `system_name`s the rail is scoped to (families expanded). */
  types: string[];
  source: RailScopeSource;
}

export interface RailSection {
  /** `core`, or `type:<system_name>`. */
  key: string;
  /** `undefined` for Core — the caller supplies the translated label. */
  systemName?: string;
  /** Resolved schema display name (falls back to the system name). */
  label?: string;
  /** Schema avatar, used instead of an invented per-type colour swatch. */
  avatar?: string;
  items: PropertyOption[];
  /** False only in search results: the type is not in the current scope. */
  inScope: boolean;
}

export interface BuildRailSectionsResult {
  sections: RailSection[];
  /** Deployed concrete types the current scope does NOT cover. */
  otherTypesCount: number;
  /**
   * The scope actually applied — `scope.types`, unless the host's fallback
   * covered the entire catalog, in which case it is empty (no real scope).
   * Drives the scope banner, which must not claim a scope the rail is not using.
   */
  effectiveTypes: string[];
}

/**
 * Resolve the types the rail should be scoped to.
 *
 * Precedence: a `type`/`label` filter anywhere in the filter being edited
 * (groups included — that is where the advanced editor puts everything) wins;
 * otherwise fall back to the host's scope (the level's eligible schemas).
 */
export function resolveRailScope(
  filter: { propertyFilters?: any[]; filterGroups?: any[] } | undefined,
  schemas: SchemaForFilters[],
  fallbackTypes: string[]
): RailScope {
  const inferred = inferEligibleSchemas(collectFilterScopeLeaves(filter ?? {}), schemas);
  if (inferred.length > 0) {
    return { types: inferred.map((s) => s.system_name), source: 'filter' };
  }
  return { types: fallbackTypes, source: 'level' };
}

/**
 * A property with no owners is universal. One that every in-scope type declares
 * is effectively universal too — promoting it to Core beats repeating it in
 * every single type section.
 *
 * That promotion needs at least two types to be shared *across*: with a single
 * type in scope it would swallow the whole type section into Core and lose the
 * provenance ("these come from Priority") the sectioning exists to show.
 */
function isCore(option: PropertyOption, scopeTypes: string[]): boolean {
  const owners = option.ownerTypes;
  if (!owners || owners.length === 0) return true;
  return scopeTypes.length > 1 && scopeTypes.every((t) => owners.includes(t));
}

function matchesQuery(option: PropertyOption, query: string): boolean {
  if (!query) return true;
  return (
    option.label.toLowerCase().includes(query) || option.key.toLowerCase().includes(query)
  );
}

export interface BuildRailSectionsConfig {
  /** The org-wide catalog — every deployed type's properties, with `ownerTypes`. */
  properties: PropertyOption[];
  scope: RailScope;
  /** Lower-cased, trimmed search query. Empty string = browse mode. */
  query: string;
  /** Schemas used to resolve type display names + avatars, and to count deployed types. */
  schemas: SchemaForFilters[];
}

/**
 * Tier the catalog into rail sections.
 *
 * Browse mode (no query) shows Core + the in-scope types only. Search mode
 * additionally surfaces matches from out-of-scope types, ordered after the
 * in-scope ones and flagged `inScope: false`.
 */
export function buildRailSections(config: BuildRailSectionsConfig): BuildRailSectionsResult {
  const { properties, scope, query, schemas } = config;
  const searching = query.length > 0;

  const byName = new Map(schemas.map((s) => [s.system_name, s]));
  const deployedTypes = schemas.filter((s) => !s.is_abstract && !s.is_hidden);

  // A host fallback that covers every deployed type is not a scope, it is the
  // absence of one (`resolveLevelEligibleSchemas` returns all schemas when a
  // level narrows to nothing). Sectioning by it would put every property in the
  // rail — precisely the "all properties" dump the tiering exists to prevent.
  // Browse Core only and let search reach the rest.
  const effectiveTypes =
    scope.source === 'level' && scope.types.length >= deployedTypes.length ? [] : scope.types;
  const otherTypesCount = Math.max(0, deployedTypes.length - effectiveTypes.length);

  const matched = properties.filter((p) => matchesQuery(p, query));

  const core: PropertyOption[] = [];
  const perType = new Map<string, PropertyOption[]>();

  matched.forEach((option) => {
    if (isCore(option, effectiveTypes)) {
      core.push(option);
      return;
    }
    const owners = option.ownerTypes ?? [];
    const inScopeOwners = owners.filter((o) => effectiveTypes.includes(o));
    // Browse mode is scoped: a property no in-scope type declares is omitted
    // entirely (that is what "never all properties" means). Search deliberately
    // reaches past the scope so nothing is unfindable.
    let targets: typeof owners = [];
    if (inScopeOwners.length > 0) targets = inScopeOwners;
    else if (searching) targets = owners;
    targets.forEach((owner) => {
      const bucket = perType.get(owner);
      if (bucket) bucket.push(option);
      else perType.set(owner, [option]);
    });
  });

  const sections: RailSection[] = [];
  if (core.length > 0) {
    sections.push({ key: 'core', items: core, inScope: true });
  }

  const typeKeys = Array.from(perType.keys());
  // In-scope types keep the scope's own order; out-of-scope matches trail them.
  const ordered = [
    ...effectiveTypes.filter((t) => perType.has(t)),
    ...typeKeys.filter((t) => !effectiveTypes.includes(t)).sort((a, b) => a.localeCompare(b)),
  ];

  ordered.forEach((systemName) => {
    const schema = byName.get(systemName);
    sections.push({
      key: `type:${systemName}`,
      systemName,
      label: schema?.name || systemName,
      avatar: schema?.avatar,
      items: perType.get(systemName) ?? [],
      inScope: effectiveTypes.includes(systemName),
    });
  });

  return { sections, otherTypesCount, effectiveTypes };
}
